/** Report calls. Shapes copied from workers/api/src/reports (merged with PR #29). Totals are the API's; the app never sums. */
import type { ApiClient } from "./client";
import { ApiError } from "./client";
import { apiBase } from "./client";
import type { SessionStore } from "./session";

export const REPORT_NAMES = ["collections", "dues", "renewals-due", "attendance", "pt-delivery", "payroll", "lead-funnel", "expenses"] as const;
export type ReportName = (typeof REPORT_NAMES)[number];

export interface ReportResult {
  report: ReportName; currency: string; locale: string; params: Record<string, unknown>; note: string | null;
  totals: Record<string, unknown>; rows: Array<Record<string, unknown>>; nextCursor: string | null;
}
export interface ReportQuery { from?: string; to?: string; branchId?: string; month?: string; groupBy?: string; limit?: number; cursor?: string }

export const fetchReport = (c: ApiClient, name: ReportName, q: ReportQuery) => c.get<ReportResult>(`/reports/${name}`, { ...q });

/**
 * CSV exports. `client.ts` parses every success body as JSON, so a CSV cannot go through it; this is the one other
 * place that reads a response, and it never stores the file: the browser downloads the blob and it is released.
 * (Deviation noted in the PR: client.ts is not this slice's file.)
 */
export async function downloadCsv(session: SessionStore, path: string, query: Record<string, string | undefined>, filename: string): Promise<void> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v) qs.set(k, v);
  const token = session.getState().token;
  let res: Response;
  try {
    res = await fetch(`${apiBase().replace(/\/$/, "")}${path}${qs.size ? `?${qs}` : ""}`, { headers: { ...(token ? { authorization: `Bearer ${token}` } : {}), accept: "text/csv" } });
  } catch {
    throw new ApiError(0, "offline", "Could not reach the server. Check your connection.");
  }
  if (!res.ok) {
    let code = "internal";
    let message = res.statusText || "Export failed";
    try {
      const b = (await res.json()) as { error?: { code?: string; message?: string } };
      code = b.error?.code ?? code;
      message = b.error?.message ?? message;
    } catch { /* not JSON */ }
    throw new ApiError(res.status, code, message, res.headers.get("x-request-id"));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** An attachment's bytes as a short-lived object URL for preview (never persisted). */
export async function fetchBlobUrl(session: SessionStore, path: string): Promise<{ url: string; type: string }> {
  const token = session.getState().token;
  const res = await fetch(`${apiBase().replace(/\/$/, "")}${path}`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new ApiError(res.status, "attachment", res.status === 404 ? "Attachment not found" : "Could not load the attachment", res.headers.get("x-request-id"));
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), type: blob.type };
}

export async function uploadAttachment(session: SessionStore, expenseId: string, file: File): Promise<void> {
  const token = session.getState().token;
  const bytes = await file.arrayBuffer();
  let res: Response;
  try {
    res = await fetch(`${apiBase().replace(/\/$/, "")}/expenses/${expenseId}/attachments?filename=${encodeURIComponent(file.name)}`, {
      method: "POST", headers: { ...(token ? { authorization: `Bearer ${token}` } : {}), "content-type": file.type || "application/octet-stream", "idempotency-key": crypto.randomUUID() }, body: bytes,
    });
  } catch {
    throw new ApiError(0, "offline", "Could not reach the server. Check your connection.");
  }
  if (!res.ok) {
    let message = "Upload failed";
    let code = "internal";
    try { const b = (await res.json()) as { error?: { code?: string; message?: string } }; message = b.error?.message ?? message; code = b.error?.code ?? code; } catch { /* */ }
    throw new ApiError(res.status, code, message, res.headers.get("x-request-id"));
  }
}
